/**
 * Right-side REEL&SHORTS status for Create Agent: analysis → edit plan → AI cut → timeline.
 * Surfaces Edit Plan (hook, scorecard, mode, cleanup) without leaving chat.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Clapperboard,
  Film,
  History,
  Loader2,
  RotateCcw,
  Subtitles,
  Volume2,
  Type,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { labelNoiseType, snippetCleanSummary } from '@/lib/reelsShortsAudioClean';
import { projectPipelineStep } from '@/lib/reelsShortsBroll';
import { reelsShortsEditorPath } from '@/lib/reelsShortsEditor';
import {
  TITLE_IMPACT_STACK,
  layerForPlacement,
  sanitizeTitleOverlay,
  splitTitleLines,
} from '@/lib/reelsShortsTitle';
import { ReelsShortsPipeline } from '@/components/reelsShorts/ReelsShortsPipeline';
import { ReelsShortsBrollLookPicker } from '@/components/reelsShorts/ReelsShortsBrollPreview';
import { ReelsShortsLayoutEngine } from '@/components/reelsShorts/ReelsShortsLayoutEngine';
import { ReelsShortsRenderVersions } from '@/components/reelsShorts/ReelsShortsRenderVersions';
import { ReelsShortsTitlePlacementPreview } from '@/components/reelsShorts/ReelsShortsTitlePlacementPreview';
import { TitleStyleControls } from '@/components/reelsShorts/ReelsShortsTitleTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const EDITING_MODES = [
  { value: 'auto', label: 'Auto Edit' },
  { value: 'creator', label: 'Creator' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'educational', label: 'Educational' },
  { value: 'news', label: 'News' },
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'viral', label: 'Viral' },
  { value: 'minimal', label: 'Minimal' },
];

const CLEANUP_LEVELS = [
  { value: 'natural', label: 'Natural' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'aggressive', label: 'Aggressive' },
];

const PLACEMENTS = [
  { value: 'top', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'bottom', label: 'Bottom' },
];

function aggregateNoise(snippets = []) {
  const types = [
    ...new Set(
      (snippets || []).flatMap((s) => s?.audio_noise_types || []).map((t) => labelNoiseType(t)).filter(Boolean),
    ),
  ];
  const cleaned = (snippets || []).filter((s) => s?.audio_cleaned).length;
  return { types, cleaned, total: (snippets || []).length };
}

export function ReelsShortsAgentPanel({
  projectId,
  job = null,
  busyStep = '',
  onOpenTranscripts,
  onOpenPlacement,
  onRunEditor,
  onRecut,
  onOpenEditor,
  onCleanAudio,
  onRetranscribe,
  onRetrySplitAv,
  refreshKey = 0,
}) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/reels-shorts/projects/${projectId}`, {
        params: { _agentFresh: Date.now() },
      });
      setProject(data?.project || null);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load reel status'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey, job?.status, job?.progress, job?.updated_at]);

  const snippets = project?.snippets || [];
  const plan = project?.edit_plan || {};
  const analysis = plan.analysis || {};
  const scorecard = plan.scorecard || null;
  const story = plan.story || {};
  const cleanup = plan.cleanup || {};
  const titleOverlay = sanitizeTitleOverlay(
    story.title_overlay || project?.editor_cut?.title_overlay,
    project?.title,
  );
  const noise = useMemo(() => aggregateNoise(snippets), [snippets]);
  const readyCount = snippets.filter(
    (s) => String(s.status || '').startsWith('ready') || String(s.transcript || '').trim(),
  ).length;
  const hasCut = Boolean((project?.editor_cut || {}).ordered_snippet_ids?.length
    || (story.ordered_snippet_ids || []).length);
  const versions = Array.isArray(project?.render_versions) ? project.render_versions : [];
  const activeStep = useMemo(() => projectPipelineStep(project, job), [project, job]);
  const jobStatus = String(job?.status || project?.status || '').toLowerCase();
  const awaitingReview = jobStatus === 'awaiting_review';
  const complete = jobStatus === 'completed' || Boolean(project?.final_video_url);
  const busy = Boolean(busyStep) || ['pending', 'running'].includes(jobStatus);

  const restoreVersion = async (row) => {
    if (!projectId || !row?.id || restoringVersionId) return;
    setRestoringVersionId(row.id);
    try {
      const { data } = await api.post(
        `/reels-shorts/projects/${projectId}/render-versions/${row.id}/restore`,
      );
      if (data?.project) setProject(data.project);
      toast.success('Restored previous export version');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not restore export version'));
    } finally {
      setRestoringVersionId(null);
    }
  };

  const patchPlanPrefs = async (patch) => {
    if (!projectId || savingPrefs) return;
    setSavingPrefs(true);
    try {
      const { data } = await api.patch(`/reels-shorts/projects/${projectId}/edit-plan`, patch);
      setProject(data?.project || null);
      toast.success('Edit plan updated');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not update edit plan'));
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-rose-400/25 bg-rose-500/[0.06] p-4 space-y-3"
      data-testid="create-plan-reels-status"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Clapperboard className="w-4 h-4 text-rose-300 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-white truncate">
              {project?.title || 'REEL&SHORTS'}
            </p>
            <p className="text-[10px] text-[#9CA3AF]">
              {snippets.length
                ? `${readyCount}/${snippets.length} clips transcribed`
                : loading
                  ? 'Loading project…'
                  : 'Waiting for clips…'}
              {analysis.video_type ? ` · ${analysis.video_type}` : ''}
            </p>
          </div>
        </div>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-rose-300" /> : null}
      </div>

      <ReelsShortsPipeline
        activeStep={activeStep}
        job={job}
        complete={complete && hasCut}
      />

      {analysis.status === 'ready' || analysis.hook?.text || analysis.topic ? (
        <div
          className="rounded-xl border border-white/10 bg-black/25 p-3 space-y-1.5"
          data-testid="create-plan-reels-analysis"
        >
          <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">AI analysis</p>
          {analysis.topic ? (
            <p className="text-xs text-white font-medium truncate">Topic · {analysis.topic}</p>
          ) : null}
          {analysis.hook?.text ? (
            <p className="text-[11px] text-[#E5E7EB] leading-snug">
              Hook · “{analysis.hook.text}”
            </p>
          ) : null}
          {(analysis.key_points || []).length ? (
            <p className="text-[10px] text-[#9CA3AF]">
              {analysis.key_points.length} key moment{(analysis.key_points.length === 1 ? '' : 's')}
              {(analysis.broll_opportunities || []).length
                ? ` · ${(analysis.broll_opportunities || []).length} B-roll opportunities`
                : ''}
            </p>
          ) : null}
        </div>
      ) : null}

      <div
        className="rounded-xl border border-white/10 bg-black/25 p-3 space-y-1.5"
        data-testid="create-plan-reels-audio-status"
      >
        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] flex items-center gap-1">
          <Volume2 className="w-3 h-3" /> Audio polish
        </p>
        {busyStep === 'clean' || busyStep === 'retranscribe' ? (
          <p className="text-[11px] text-rose-200 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            {busyStep === 'clean' ? 'Detecting noise & cleaning…' : 'Re-transcribing cleaned audio…'}
          </p>
        ) : noise.cleaned ? (
          <p className="text-[11px] text-[#E5E7EB] leading-snug">
            {noise.cleaned}/{noise.total || noise.cleaned} clips cleaned
            {noise.types.length ? ` · ${noise.types.join(', ')}` : ''}
          </p>
        ) : (
          <p className="text-[11px] text-[#9CA3AF]">
            Detect noise type, clean, then re-transcribe for accurate subtitles.
          </p>
        )}
        {snippets.slice(0, 3).map((s) => {
          const summary = snippetCleanSummary(s);
          if (!summary) return null;
          return (
            <p key={s.id} className="text-[10px] text-[#9CA3AF] truncate">
              {s.filename || s.id}: {summary}
            </p>
          );
        })}
      </div>

      {titleOverlay.enabled || titleOverlay.duration_seconds > 0 || titleOverlay.placement ? (
        <div
          className="rounded-xl border border-white/10 bg-black/25 p-3 space-y-1"
          data-testid="create-plan-reels-placement-status"
        >
          <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] flex items-center gap-1">
            <Type className="w-3 h-3" /> Title / subtitle placement
          </p>
          <p className="text-[11px] text-[#E5E7EB] leading-snug">
            Title {titleOverlay.placement || 'top'}
            {titleOverlay.duration_seconds > 0
              ? ` · first ${titleOverlay.duration_seconds}s`
              : ' · full clip'}
            {' · '}
            captions {titleOverlay.caption_placement || 'bottom'}
          </p>
          {titleOverlay.line1 ? (
            <p className="text-[10px] text-[#9CA3AF] truncate">
              “{titleOverlay.line1}{titleOverlay.line2 ? ` / ${titleOverlay.line2}` : ''}”
            </p>
          ) : null}
        </div>
      ) : null}

      {scorecard ? (
        <div
          className="grid grid-cols-2 gap-1.5"
          data-testid="create-plan-reels-scorecard"
        >
          {[
            ['Hook', scorecard.hook],
            ['Visuals', scorecard.visual_variety],
            ['Captions', `${scorecard.caption_coverage_pct ?? 0}%`],
            ['B-roll', `${scorecard.broll_coverage_pct ?? 0}%`],
            ['Dead air', `${scorecard.dead_air_pct ?? 0}%`],
            ['CTA', scorecard.cta],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5">
              <p className="text-[9px] uppercase tracking-wide text-[#6B7280]">{label}</p>
              <p className="text-[11px] text-white font-medium truncate">{value}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Editing mode</span>
          <Select
            value={story.editing_mode || project?.editing_mode || 'auto'}
            onValueChange={(v) => patchPlanPrefs({ editing_mode: v })}
            disabled={savingPrefs}
          >
            <SelectTrigger className="h-8 text-[11px] bg-black/30 border-white/15" data-testid="create-plan-reels-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[240]">
              {EDITING_MODES.map((m) => (
                <SelectItem key={m.value} value={m.value} className="text-xs">{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Cleanup</span>
          <Select
            value={cleanup.level || project?.cleanup_level || 'balanced'}
            onValueChange={(v) => patchPlanPrefs({ cleanup_level: v })}
            disabled={savingPrefs}
          >
            <SelectTrigger className="h-8 text-[11px] bg-black/30 border-white/15" data-testid="create-plan-reels-cleanup">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[240]">
              {CLEANUP_LEVELS.map((m) => (
                <SelectItem key={m.value} value={m.value} className="text-xs">{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>

      {projectId ? (
        <ReelsShortsLayoutEngine
          projectId={projectId}
          formats={plan.formats || project?.edit_plan?.formats}
          disabled={savingPrefs || ['pending', 'running'].includes(jobStatus)}
          compact
          onUpdated={(next) => {
            if (next) setProject(next);
          }}
        />
      ) : null}

      {job?.message ? (
        <p className="text-[11px] text-[#E5E7EB] leading-snug">{job.message}</p>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!snippets.length || busyStep === 'clean' || busyStep === 'retranscribe'}
          onClick={onCleanAudio}
          className="text-[11px] h-8"
          data-testid="create-plan-reels-clean-audio"
        >
          {busyStep === 'clean' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
          ) : (
            <Volume2 className="w-3.5 h-3.5 mr-1" />
          )}
          Detect & clean
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!snippets.length || busyStep === 'retranscribe' || busyStep === 'clean'}
          onClick={onRetranscribe}
          className="text-[11px] h-8"
          data-testid="create-plan-reels-retranscribe"
        >
          {busyStep === 'retranscribe' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
          ) : (
            <Subtitles className="w-3.5 h-3.5 mr-1" />
          )}
          Retranscribe
        </Button>
        {onRetrySplitAv ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!snippets.length || busy || ['pending', 'running'].includes(jobStatus)}
            onClick={onRetrySplitAv}
            className="text-[11px] h-8"
            title="Re-run Split A/V + transcribe. Rebuilds mixed + per-speaker audio tracks."
            data-testid="create-plan-reels-retry-split-av"
          >
            {busyStep === 'retry-split' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            ) : (
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
            )}
            Retry Split A/V
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!snippets.length || busyStep === 'transcripts'}
          onClick={onOpenTranscripts}
          className="text-[11px] h-8"
          data-testid="create-plan-reels-review-transcripts"
        >
          {busyStep === 'transcripts' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
          ) : (
            <Subtitles className="w-3.5 h-3.5 mr-1" />
          )}
          Review subtitles
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!projectId}
          onClick={onOpenPlacement}
          className="text-[11px] h-8"
          data-testid="create-plan-reels-placement"
        >
          <Type className="w-3.5 h-3.5 mr-1" />
          Title placement
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!readyCount || busyStep === 'editor' || busyStep === 'recut' || ['pending', 'running'].includes(jobStatus)}
          onClick={onRunEditor}
          className="text-[11px] h-8 bg-rose-500 hover:bg-rose-400 text-black"
          data-testid="create-plan-reels-run-editor"
        >
          {busyStep === 'editor' ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
          ) : (
            <TwinneeLogo className="w-3.5 h-3.5 mr-1" />
          )}
          {awaitingReview || !hasCut ? 'Generate edit' : 'Re-generate edit'}
        </Button>
        {onRecut ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={!readyCount || busy || busyStep === 'recut'}
            onClick={onRecut}
            className="text-[11px] h-8"
            title="Re-run AI editor on the same clips and export again. Prior MP4s stay in history."
            data-testid="create-plan-reels-recut"
          >
            {busyStep === 'recut' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            ) : (
              <History className="w-3.5 h-3.5 mr-1" />
            )}
            {complete || versions.length ? 'Re-cut polished reel' : 'Cut polished reel'}
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={!projectId}
          onClick={() => onOpenEditor?.(reelsShortsEditorPath(projectId))}
          className="text-[11px] h-8"
          data-testid="create-plan-reels-open-editor"
        >
          <Film className="w-3.5 h-3.5 mr-1" />
          Open timeline
        </Button>
      </div>

      {versions.length ? (
        <ReelsShortsRenderVersions
          versions={versions}
          compact
          onRestore={restoreVersion}
          restoringId={restoringVersionId}
          disabled={busy}
        />
      ) : null}
    </div>
  );
}

/** Popup: edit clip transcripts + optional B-ROLL look before generating the edit plan. */
export function ReelsShortsTranscriptDialog({
  open,
  projectId,
  onClose,
  onSaved,
}) {
  const [project, setProject] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [brollStyle, setBrollStyle] = useState('photoreal_cinematic');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/reels-shorts/projects/${projectId}`);
        if (cancelled) return;
        const proj = data?.project || null;
        setProject(proj);
        const next = {};
        (proj?.snippets || []).forEach((s) => {
          next[s.id] = String(s.transcript || '');
        });
        setDrafts(next);
        setBrollStyle(
          String(
            proj?.edit_plan?.story?.visual_style
              || proj?.editor_cut?.broll_visual_style
              || proj?.input?.broll_visual_style
              || 'photoreal_cinematic',
          ),
        );
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err, 'Could not load transcripts'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  if (!open) return null;

  const snippets = project?.snippets || [];

  const save = async () => {
    if (!projectId || saving) return;
    setSaving(true);
    try {
      for (const s of snippets) {
        const next = String(drafts[s.id] ?? s.transcript ?? '');
        if (next === String(s.transcript || '')) continue;
        // eslint-disable-next-line no-await-in-loop
        await api.patch(`/reels-shorts/projects/${projectId}/snippets/${s.id}/transcript`, {
          transcript: next,
        });
      }
      await api.patch(`/reels-shorts/projects/${projectId}/edit-plan`, {
        visual_style: brollStyle,
        refresh_analysis: true,
      });
      toast.success('Saved — next: choose title / subtitle placement');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save transcripts'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[220] flex items-end sm:items-center justify-center bg-black/70 p-3"
      data-testid="create-plan-reels-transcript-popup"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl border border-white/15 bg-[#0d1017] shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-white">Review subtitles</p>
            <p className="text-[11px] text-[#9CA3AF]">
              Edit spoken text after audio clean / re-transcribe, then set title placement.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[#9CA3AF] hover:text-white cursor-pointer"
          >
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading clips…
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <p className="text-[11px] font-semibold text-[#E5E7EB]">B-ROLL look</p>
                <ReelsShortsBrollLookPicker
                  compact
                  value={brollStyle}
                  onChange={setBrollStyle}
                  disabled={saving}
                  testId="create-plan-reels-broll-look"
                />
              </div>
              {snippets.map((s, idx) => (
                <label key={s.id} className="block space-y-1">
                  <span className="text-[11px] font-semibold text-[#CBD5E1]">
                    Clip {idx + 1}
                    {s.filename ? ` · ${s.filename}` : ''}
                    {s.audio_cleaned ? (
                      <span className="ml-1 text-[10px] text-emerald-300/80">
                        · cleaned{snippetCleanSummary(s) ? ` (${snippetCleanSummary(s)})` : ''}
                      </span>
                    ) : null}
                  </span>
                  <textarea
                    rows={3}
                    value={drafts[s.id] ?? ''}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [s.id]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl text-xs bg-black/40 border border-white/15 text-white placeholder:text-[#6B7280] focus:outline-none focus:border-rose-400/60 resize-y"
                  />
                </label>
              ))}
              {!snippets.length ? (
                <p className="text-xs text-[#9CA3AF]">No clips yet — wait for split/transcribe to finish.</p>
              ) : null}
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-white/10 flex justify-end gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onClose} className="text-xs">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={saving || loading || !snippets.length}
            className="text-xs bg-rose-500 hover:bg-rose-400 text-black"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            Save & continue
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Popup: title + subtitle placement for the first N seconds. */
export function ReelsShortsPlacementDialog({
  open,
  projectId,
  onClose,
  onSaved,
}) {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [templateId, setTemplateId] = useState(TITLE_IMPACT_STACK);
  const [placement, setPlacement] = useState('top');
  const [captionPlacement, setCaptionPlacement] = useState('bottom');
  const [durationSeconds, setDurationSeconds] = useState(5);
  const [layer, setLayer] = useState(() => layerForPlacement('top'));
  const [style, setStyle] = useState(() => sanitizeTitleOverlay({}).style);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/reels-shorts/projects/${projectId}`);
        if (cancelled) return;
        const proj = data?.project || null;
        setProject(proj);
        const overlay = sanitizeTitleOverlay(
          proj?.edit_plan?.story?.title_overlay || proj?.editor_cut?.title_overlay,
          proj?.title,
        );
        const hook = String(proj?.edit_plan?.analysis?.hook?.text || '').trim();
        const fallback = hook || String(proj?.title || '');
        const split = splitTitleLines(fallback);
        setEnabled(overlay.enabled || Boolean(overlay.line1 || overlay.line2 || fallback));
        setLine1(overlay.line1 || split.line1);
        setLine2(overlay.line2 || split.line2);
        setTemplateId(overlay.template_id || TITLE_IMPACT_STACK);
        setPlacement(overlay.placement || 'top');
        setCaptionPlacement(overlay.caption_placement || 'bottom');
        setLayer(overlay.layer || layerForPlacement(overlay.placement || 'top'));
        setStyle(overlay.style || sanitizeTitleOverlay({}).style);
        setDurationSeconds(
          overlay.duration_seconds > 0 ? overlay.duration_seconds : 5,
        );
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err, 'Could not load placement'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const previewUrl = useMemo(() => {
    const snips = project?.snippets || [];
    const ready = snips.find((s) => s?.video_s3_url || s?.original_s3_url);
    return String(ready?.video_s3_url || ready?.original_s3_url || '').trim();
  }, [project]);

  if (!open) return null;

  const save = async () => {
    if (!projectId || saving) return;
    setSaving(true);
    try {
      const overlay = sanitizeTitleOverlay({
        enabled,
        line1,
        line2,
        template_id: templateId,
        placement,
        caption_placement: captionPlacement,
        duration_seconds: Number(durationSeconds) || 5,
        layer,
        style,
      }, project?.title);
      await api.patch(`/reels-shorts/projects/${projectId}/edit-plan`, {
        title_overlay: overlay,
      });
      toast.success('Title & subtitle placement saved');
      onSaved?.(overlay);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save placement'));
    } finally {
      setSaving(false);
    }
  };

  const applyPlacement = (next) => {
    setPlacement(next);
    setLayer(layerForPlacement(next));
  };

  return (
    <div
      className="fixed inset-0 z-[220] flex items-end sm:items-center justify-center bg-black/70 p-3"
      data-testid="create-plan-reels-placement-popup"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/15 bg-[#0d1017] shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-white">Title & subtitle placement</p>
            <p className="text-[11px] text-[#9CA3AF]">
              Preview on the reel — drag, resize, or swap the hook title. Subtitles snap top / center / bottom.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-[#9CA3AF] hover:text-white cursor-pointer"
          >
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-[#9CA3AF]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <ReelsShortsTitlePlacementPreview
                enabled={enabled}
                line1={line1}
                line2={line2}
                templateId={templateId}
                style={style}
                layer={layer}
                captionPlacement={captionPlacement}
                previewUrl={previewUrl}
                onLayerChange={(nextLayer, nextPlacement) => {
                  setLayer(nextLayer);
                  if (nextPlacement) setPlacement(nextPlacement);
                }}
                onCaptionPlacementChange={setCaptionPlacement}
                onTemplateChange={setTemplateId}
                onLinesChange={({ line1: a, line2: b }) => {
                  setLine1(a);
                  setLine2(b);
                  if (a.trim() || b.trim()) setEnabled(true);
                }}
              />

              <label className="flex items-center gap-2 text-xs text-[#E5E7EB]">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="rounded border-white/20"
                />
                Show hook title on the reel
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Line 1</span>
                  <Input
                    value={line1}
                    onChange={(e) => setLine1(e.target.value)}
                    className="h-9 text-xs bg-black/40 border-white/15"
                    maxLength={80}
                    data-testid="create-plan-reels-title-line1"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Line 2</span>
                  <Input
                    value={line2}
                    onChange={(e) => setLine2(e.target.value)}
                    className="h-9 text-xs bg-black/40 border-white/15"
                    maxLength={80}
                    data-testid="create-plan-reels-title-line2"
                  />
                </label>
              </div>
              <TitleStyleControls
                style={style}
                onChange={(next) => setStyle(next)}
              />
              <div className="grid grid-cols-2 gap-2">
                <label className="space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Title placement</span>
                  <Select value={placement} onValueChange={applyPlacement}>
                    <SelectTrigger
                      className="h-9 text-xs bg-black/30 border-white/15"
                      data-testid="create-plan-reels-title-placement"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[240]">
                      {PLACEMENTS.map((p) => (
                        <SelectItem key={p.value} value={p.value} className="text-xs">{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">Subtitle placement</span>
                  <Select value={captionPlacement} onValueChange={setCaptionPlacement}>
                    <SelectTrigger
                      className="h-9 text-xs bg-black/30 border-white/15"
                      data-testid="create-plan-reels-caption-placement"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[240]">
                      {PLACEMENTS.map((p) => (
                        <SelectItem key={p.value} value={p.value} className="text-xs">{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
              </div>
              <label className="block space-y-1">
                <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">
                  Show title for first (seconds)
                </span>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  step={1}
                  value={durationSeconds}
                  onChange={(e) => setDurationSeconds(e.target.value)}
                  className="h-9 text-xs bg-black/40 border-white/15"
                  data-testid="create-plan-reels-title-duration"
                />
                <p className="text-[10px] text-[#6B7280]">
                  Hook title appears only for the first N seconds; subtitles keep their spoken timing.
                </p>
              </label>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-white/10 flex justify-end gap-2">
          <Button type="button" size="sm" variant="secondary" onClick={onClose} className="text-xs">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={saving || loading}
            className="text-xs bg-rose-500 hover:bg-rose-400 text-black"
            data-testid="create-plan-reels-placement-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            Save placement
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ReelsShortsAgentPanel;
